import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PressWork, FileRecord } from '@twinsketch/topika-model';
import ErrorModal from 'src/components/common/ErrorModal';
import 'src/styles/workOrder/work-order.css';

interface WorkOrderPressWorkFormProps {
  pressWorks: PressWork[];
  files: FileRecord[];
}

const WorkOrderPressWorkForm: React.FC<WorkOrderPressWorkFormProps> = ({ pressWorks, files }) => {
  const { id } = useParams<{ id?: string }>();
  const [error, setError] = useState<string | null>(null);
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());

  const toggleExpand = (index: number) => {
    setExpandedIndices(prev => {
      const updated = new Set(prev);
      if (updated.has(index)) {
        updated.delete(index);
      } else {
        updated.add(index);
      }
      return updated;
    });
  };

  return (
    <div className="content-wrapper">
      <ErrorModal message={error} onClose={() => setError(null)} />

      <h2>Press Summary</h2>
      <div className="table-wrapper">
        <table className="mixing-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th></th>
              <th>Date</th>
              <th>Operators</th>
              <th>Project</th>
              <th>Ref. Filename</th>
            </tr>
          </thead>
          <tbody>
            {pressWorks.length > 0 ? (
              pressWorks.map((work, index) => {
                const extractedFrom = work.extractedFrom;
                const matchedFile = extractedFrom?.sourceFileId
                  ? files.find(file => file.id === extractedFrom?.sourceFileId)
                  : null;

                return (
                  <React.Fragment key={index}>
                    <tr>
                      <td>
                        <button
                          onClick={() => toggleExpand(index)}
                          aria-label={`Toggle details for entry ${index + 1}`}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            color: 'brown',
                          }}
                        >
                          {expandedIndices.has(index) ? '−' : '+'}
                        </button>
                      </td>
                      <td>{work.date ?? '—'}</td>
                      <td>{work.operators?.join(' | ') ?? '—'}</td>
                      <td>{work.projectName ?? '—'}</td>
                      <td>
                        {matchedFile ? (
                          <span style={{ color: 'green' }}>{matchedFile.fileName}</span>
                        ) : (
                          <span style={{ color: 'gray' }}>No File Available</span>
                        )}
                      </td>
                    </tr>

                    {expandedIndices.has(index) && (
                      <tr>
                        <td colSpan={5} style={{ padding: 0 }}>
                          <div
                            style={{
                              padding: '12px',
                              backgroundColor: '#fafafa',
                              borderTop: '1px solid #ccc',
                            }}
                          >
                            <strong style={{ display: 'block', marginBottom: '8px' }}>
                              Press Batch
                            </strong>
                            <div>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Batch No</th>
                                    <th>Coating Output (M)</th>
                                    <th>Press Output (M)</th>
                                    <th>Main Temp (℃)</th>
                                    <th>Speed (m/min)</th>
                                    <th>Unwind (N)</th>
                                    <th>Of (N)</th>
                                    <th>Rewind (N)</th>
                                    <th>Hydraulic (Ton/cm)</th>
                                    <th>Lot No(s)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {work.batches.map((batch, index) => (
                                    <tr key={index}>
                                      <td>{batch.batchNo ?? '—'}</td>
                                      <td>{batch.coatingOutputMeter ?? '—'}</td>
                                      <td>{batch.pressOutputMeter ?? '—'}</td>
                                      <td>{batch.mainTempCelcius ?? '—'}</td>
                                      <td>{batch.speedMeterPerMin ?? '—'}</td>
                                      <td>{batch.unwindTensionN ?? '—'}</td>
                                      <td>{batch.offsetTensionN ?? '—'}</td>
                                      <td>{batch.rewindTensionN ?? '—'}</td>
                                      <td>{batch.hydraulicTonPerCm ?? '—'}</td>
                                      <td>{batch.coatingLotNos?.join(', ') ?? '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div
                            style={{
                              padding: '12px',
                              backgroundColor: '#fafafa',
                              borderTop: '1px solid #ccc',
                            }}
                          >
                            <strong style={{ display: 'block', marginBottom: '8px' }}>
                              Press Eval
                            </strong>

                            <div>
                              <table>
                                <thead>
                                  <tr>
                                    <th rowSpan={2}>No</th>
                                    <th rowSpan={2}>Thickness</th>
                                    <th>Start</th>
                                    <th>End</th>
                                    <th rowSpan={2}>Min</th>
                                    <th rowSpan={2}>Max</th>
                                    <th rowSpan={2}>Press %</th>
                                    <th rowSpan={2}>Density</th>
                                    <th colSpan={3}>Evaluation</th>
                                    <th rowSpan={2}>Status</th>
                                    <th rowSpan={2}>Notes</th>
                                  </tr>
                                  <tr>
                                    <th>Start</th>
                                    <th>End</th>
                                    <th>CPK</th>
                                    <th>CP</th>
                                    <th>STDEV</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {work.evaluations.map((evalData, eIndex) => (
                                    <tr key={eIndex}>
                                      <td>{evalData.no}</td>
                                      <td>{evalData.coatingThickness}</td>
                                      <td>
                                        {evalData.pressThinkness?.entryMeasurements?.center?.join(
                                          ', '
                                        )}
                                      </td>
                                      <td>
                                        {evalData.pressThinkness?.exitMeasurements?.center?.join(
                                          ', '
                                        )}
                                      </td>
                                      <td>{evalData.pressRatePercentage}</td>
                                      <td>{evalData.bulkDensity}</td>
                                      <td>{evalData.evaluationMetrics?.cpk}</td>
                                      <td>{evalData.evaluationMetrics?.cp}</td>
                                      <td>{evalData.evaluationMetrics?.stdev}</td>
                                      <td>{evalData.surfaceStatus}</td>
                                      <td>{evalData.remarks ?? '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={12} style={{ textAlign: 'center', color: 'gray' }}>
                  No Mixing Work Available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkOrderPressWorkForm;
